import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, auth } from '../firebase/config'; // Ajusta la ruta a tu archivo de config de Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';

// Creamos el contexto exclusivo para Facturación & Finanzas
const FacturacionDataContext = createContext(null);

/**
 * Proveedor de tablas maestras financieras con sincronización en tiempo real (onSnapshot).
 * Escucha únicamente las colecciones exactas de la empresa para Facturación, Gastos y Tesorería.
 */
export const FacturacionDataProvider = ({ children }) => {
    const [facturacionMasters, setFacturacionMasters] = useState({
        clientes: [],
        proveedores: [],
        cuentas: [],
        areas: [],
        conceptosFacturas: [],
        conceptosGastos: []
    });

    const [loadingFacturacionMasters, setLoadingFacturacionMasters] = useState(true);

    useEffect(() => {
        let activeUnsubscribes = [];

        const cleanActiveListeners = () => {
            activeUnsubscribes.forEach(unsub => {
                if (typeof unsub === 'function') unsub();
            });
            activeUnsubscribes = [];
        };

        // --- ESCUDO DE AUTENTICACIÓN (onAuthStateChanged) ---
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            cleanActiveListeners();

            if (!user) {
                setFacturacionMasters({
                    clientes: [],
                    proveedores: [],
                    cuentas: [],
                    areas: [],
                    conceptosFacturas: [],
                    conceptosGastos: []
                });
                setLoadingFacturacionMasters(false);
                return;
            }

            setLoadingFacturacionMasters(true);

            // Control de sincronización inicial
            const initialLoadTracker = {
                clientes: false,
                proveedores: false,
                cuentas: false,
                areas: false,
                conceptosFacturas: false,
                conceptosGastos: false
            };

            const checkAllLoaded = () => {
                const allLoaded = Object.values(initialLoadTracker).every(Boolean);
                if (allLoaded) {
                    setLoadingFacturacionMasters(false);
                }
            };

            // 1. Clientes (Facturado A) -> public/data/empresa_clientes
            const unsubClientes = onSnapshot(collection(db, 'public/data/empresa_clientes'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    clientes: list.sort((a, b) => (a.nombre || a.razonSocial || '').localeCompare(b.nombre || b.razonSocial || ''))
                }));
                initialLoadTracker.clientes = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_clientes):", err);
                initialLoadTracker.clientes = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubClientes);

            // 2. Proveedores / Fisco -> public/data/empresa_proveedores
            const unsubProveedores = onSnapshot(collection(db, 'public/data/empresa_proveedores'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    proveedores: list.sort((a, b) => (a.nombre || a.razonSocial || '').localeCompare(b.nombre || b.razonSocial || ''))
                }));
                initialLoadTracker.proveedores = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_proveedores):", err);
                initialLoadTracker.proveedores = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubProveedores);

            // 3. Cuentas Bancarias / CBU -> public/data/empresa_cuentas
            const unsubCuentas = onSnapshot(collection(db, 'public/data/empresa_cuentas'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    cuentas: list.sort((a, b) => (a.nombre || a.banco || '').localeCompare(b.nombre || b.banco || ''))
                }));
                initialLoadTracker.cuentas = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_cuentas):", err);
                initialLoadTracker.cuentas = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubCuentas);

            // 4. Áreas Presupuestarias / Centros de Costo -> public/data/empresa_areas
            const unsubAreas = onSnapshot(collection(db, 'public/data/empresa_areas'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    areas: list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                }));
                initialLoadTracker.areas = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_areas):", err);
                initialLoadTracker.areas = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubAreas);

            // 5. Conceptos de Facturas Emitidas -> public/data/empresa_conceptos_facturas
            const unsubConceptosFacturas = onSnapshot(collection(db, 'public/data/empresa_conceptos_facturas'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    conceptosFacturas: list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                }));
                initialLoadTracker.conceptosFacturas = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_conceptos_facturas):", err);
                initialLoadTracker.conceptosFacturas = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubConceptosFacturas);

            // 6. Conceptos de Gastos / Compras -> public/data/empresa_conceptos_gastos
            const unsubConceptosGastos = onSnapshot(collection(db, 'public/data/empresa_conceptos_gastos'), (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setFacturacionMasters(prev => ({
                    ...prev,
                    conceptosGastos: list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                }));
                initialLoadTracker.conceptosGastos = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (empresa_conceptos_gastos):", err);
                initialLoadTracker.conceptosGastos = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubConceptosGastos);
        });

        return () => {
            unsubscribeAuth();
            cleanActiveListeners();
        };
    }, []);

    return (
        <FacturacionDataContext.Provider value={{ facturacionMasters, loadingFacturacionMasters }}>
            {children}
        </FacturacionDataContext.Provider>
    );
};

export const useFacturacionData = () => {
    const context = useContext(FacturacionDataContext);
    if (!context) {
        throw new Error("useFacturacionData debe ser utilizado obligatoriamente dentro de un FacturacionDataProvider");
    }
    return context;
};